import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toast } from 'react-bootstrap';
import './PatientList.css';
import './DoctorInformation.css';
import './TestInformation.css';

const API_URL = process.env.REACT_APP_API_URL;
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAY_LABELS = {
  Monday: 'الاثنين',
  Tuesday: 'الثلاثاء',
  Wednesday: 'الأربعاء',
  Thursday: 'الخميس',
  Friday: 'الجمعة',
  Saturday: 'السبت',
  Sunday: 'الأحد'
};
// Hour list options (00:00, 00:30, ..., 23:30)
const HOUR_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});
// Normalize backend time values to HH:MM (drop seconds, pad hour/min)
const toHHMM = (val) => {
  if (!val) return '';
  // e.g., "9:00", "09:00:00", "09:30" -> keep HH:MM
  const parts = String(val).split(':');
  const h = parts[0] ? String(parseInt(parts[0], 10)).padStart(2, '0') : '00';
  const m = parts[1] ? String(parseInt(parts[1], 10)).padStart(2, '0') : '00';
  return `${h}:${m}`;
};
// Convert HH:MM (24h) to 12-hour with AM/PM for display
const to12h = (val) => {
  const hhmm = toHHMM(val);
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'مساءا' : 'صباحا';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
};

const TestInformation = () => {
  const [doctor, setDoctor] = useState({
    full_name: '',
    gender: 'male',
    email: '',
    phone_number: '',
    field_id: 1, // Default field ID, adjust as needed
    brief: '',
    license_number: '',
    license_date: '',
    consultation_fee: 0,
    clinic_consultation_fee: 0,
    is_phone: true,
    is_clinic: true,
    clinic_address: '',
    info: '',
    facebook_url: '',
    instagram_url: '',
    per_30min_capacity: 4,
    booking_mode: 'day',
    username: '',
    open_days: [],
    open_day_times: {}
  });
  const [clinicLocation, setClinicLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [copyingQR, setCopyingQR] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  // Closed dates (clinic overrides)
  const [closedDates, setClosedDates] = useState([]); // [{id, date}]
  const [newClosedDate, setNewClosedDate] = useState(''); // YYYY-MM-DD
  const navigate = useNavigate();

  const showMessage = (message, error = false) => {
    setToastMessage(message);
    setIsError(error);
    setShowToast(true);
  };

  useEffect(() => {
    const getCurrentLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCurrentLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            setLocationLoading(false);
          },
          (error) => {
            console.log('Geolocation error:', error);
            setLocationLoading(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        setLocationLoading(false);
      }
    };

    const fetchDoctorInfo = async () => {
      try {
                
        const response = await axios.get(`${API_URL}doctor/get-doctor`, {
          headers: { 'Accept': 'application/json' }
        });
        
        const data = response.data.data || response.data;
        // Normalize open_days from API: it may be an array of strings (old) or array of objects (new)
        let openDaysList = [];
        let timesMap = {};
        if (Array.isArray(data.open_days)) {
          if (data.open_days.length && typeof data.open_days[0] === 'object') {
            // New format: [{ day_name, opening_time, closing_time }, ...]
            openDaysList = data.open_days.map(d => d.day_name).filter(Boolean);
            timesMap = data.open_days.reduce((acc, d) => {
              if (d && d.day_name) {
                acc[d.day_name] = {
                  opening_time: toHHMM(d.opening_time || ''),
                  closing_time: toHHMM(d.closing_time || '')
                };
              }
              return acc;
            }, {});
          } else {
            // Old format: ["Monday", "Tuesday", ...]
            openDaysList = data.open_days;
            const legacy = data.open_day_times || {};
            timesMap = Object.keys(legacy).reduce((acc, k) => {
              acc[k] = {
                opening_time: toHHMM(legacy[k]?.opening_time || ''),
                closing_time: toHHMM(legacy[k]?.closing_time || ''),
              };
              return acc;
            }, {});
          }
        }

        setDoctor({
          full_name: data.full_name || '',
          gender: data.gender || 'male',
          email: data.email || '',
          phone_number: data.phone_number || '',
          brief: data.brief || '',
          license_number: data.license_number || '',
          license_date: data.license_date || '',
          clinic_address: data.clinic_address || '',
          clinic_consultation_fee: data.clinic_consultation_fee ?? 0,
          is_clinic: data.is_clinic ?? false,
          facebook_url: data.facebook_url || '',
          instagram_url: data.instagram_url || '',
          per_30min_capacity: (typeof data.per_30min_capacity !== 'undefined' && data.per_30min_capacity !== null) ? data.per_30min_capacity : 4,
          booking_mode: data.booking_mode || 'day',
          username: data.username || '',
          open_days: openDaysList,
          open_day_times: timesMap
        });
        // Initialize closed dates if provided, then refresh from API for consistency
        if (Array.isArray(data.closed_dates)) {
          setClosedDates((data.closed_dates || []).map(d => ({ id: null, date: d })));
        }
        await loadClosedDates();
        
        // Load saved clinic location if available, otherwise get current location for display
        if (data.clinic_latitude && data.clinic_longitude) {
          setClinicLocation({
            latitude: parseFloat(data.clinic_latitude),
            longitude: parseFloat(data.clinic_longitude)
          });
          setLocationLoading(false);
        } else {
          getCurrentLocation();
        }
      } catch (error) {
        console.error('Error fetching doctor information:', error);
        showMessage('Failed to load doctor information', true);
        setLocationLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorInfo();
  }, [navigate]);

  // Load closed dates list for authenticated doctor
  const loadClosedDates = async () => {
    try {
                  const res = await axios.get(`${API_URL}doctor/closed-dates`, {
        headers: { 'Accept': 'application/json' }
      });
      const payload = res.data.data || [];
      // payload expected: [{id, date}]
      setClosedDates(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error('Failed to load closed dates', err);
    }
  };

  const handleAddClosedDate = async (e) => {
    e?.preventDefault?.();
    if (!newClosedDate) {
      showMessage('يرجى اختيار التاريخ لإضافته', true);
      return;
    }
    try {
            await axios.post(`${API_URL}doctor/closed-dates`, { date: newClosedDate }, {
        headers: { 'Accept': 'application/json' }
      });
      setNewClosedDate('');
      await loadClosedDates();
      showMessage('تمت إضافة يوم الإغلاق بنجاح');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'فشل في إضافة يوم الإغلاق';
      showMessage(msg, true);
    }
  };

  const handleDeleteClosedDate = async (id) => {
    if (!id) return;
    if (!window.confirm('هل أنت متأكد من حذف هذا اليوم؟')) return;
    try {
            await axios.delete(`${API_URL}doctor/closed-dates/${id}`, {
        headers: { 'Accept': 'application/json' }
      });
      await loadClosedDates();
      showMessage('تم حذف يوم الإغلاق');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'فشل في حذف يوم الإغلاق';
      showMessage(msg, true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDoctor(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleOpenDay = (day) => {
    setDoctor(prev => {
      const exists = prev.open_days?.includes(day);
      const next = exists
        ? prev.open_days.filter(d => d !== day)
        : [...(prev.open_days || []), day];
      // maintain times: remove times when day is unchecked
      const nextTimes = { ...(prev.open_day_times || {}) };
      if (!exists) {
        // ensure time object exists when adding
        if (!nextTimes[day]) nextTimes[day] = { opening_time: '', closing_time: '' };
      } else {
        delete nextTimes[day];
      }
      return { ...prev, open_days: next, open_day_times: nextTimes };
    });
  };

  const selectAllOpenDays = () => {
    setDoctor(prev => ({ ...prev, open_days: [...DAYS] }));
  };

  const clearOpenDays = () => {
    setDoctor(prev => ({ ...prev, open_days: [], open_day_times: {} }));
  };

  const handleTimeChange = (day, field, value) => {
    setDoctor(prev => {
      const times = { ...(prev.open_day_times || {}) };
      if (!times[day]) times[day] = { opening_time: '', closing_time: '' };
      times[day] = { ...times[day], [field]: value };
      return { ...prev, open_day_times: times };
    });
  };

  // Google Maps component
  const GoogleMapComponent = ({ center, clinicLocation, onMapClick }) => {
    const mapRef = useRef();
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);

    useEffect(() => {
      const initializeMap = () => {
        if (mapRef.current && !mapInstanceRef.current && window.google && window.google.maps) {
          const googleMap = new window.google.maps.Map(mapRef.current, {
            center: center,
            zoom: 13,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
          });
          
          mapInstanceRef.current = googleMap;
          
          // Add click listener
          googleMap.addListener('click', (e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            onMapClick({ latitude: lat, longitude: lng });
          });
          
          // If there's already a clinic location, show marker immediately
          if (clinicLocation) {
            const marker = new window.google.maps.Marker({
              position: { lat: clinicLocation.latitude, lng: clinicLocation.longitude },
              map: googleMap,
              title: 'Clinic Location',
              animation: window.google.maps.Animation.DROP
            });
            markerRef.current = marker;
          }
        }
      };
      
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        initializeMap();
      } else {
        // Wait for Google Maps to load
        const checkGoogleMaps = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkGoogleMaps);
            initializeMap();
          }
        }, 100);
        
        // Cleanup interval after 10 seconds
        setTimeout(() => clearInterval(checkGoogleMaps), 10000);
      }
    }, [center, onMapClick, clinicLocation]);

    useEffect(() => {
      if (mapInstanceRef.current && clinicLocation && window.google && window.google.maps) {
        // Remove existing marker
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        
        // Add new marker
        const newMarker = new window.google.maps.Marker({
          position: { lat: clinicLocation.latitude, lng: clinicLocation.longitude },
          map: mapInstanceRef.current,
          title: 'Clinic Location',
          animation: window.google.maps.Animation.DROP
        });
        
        markerRef.current = newMarker;
        
        // Center map on marker
        mapInstanceRef.current.setCenter({ lat: clinicLocation.latitude, lng: clinicLocation.longitude });
      }
    }, [clinicLocation]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
      };
    }, []);

    return <div ref={mapRef} style={{ height: '100%', width: '100%' }} />;
  };

  const resetToCurrentLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setClinicLocation(newLocation);
          setCurrentLocation(newLocation);
          setLocationLoading(false);
          alert('تم تحديد الموقع الحالي كموقع');
        },
        (error) => {
          console.log('Geolocation error:', error);
          setLocationLoading(false);
          alert('لا يمكن الحصول على الموقع الحالي');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0 // Force fresh location
        }
      );
    } else {
      setLocationLoading(false);
      alert('المتصفح لا يدعم تحديد الموقع');
    }
  };

  const clearLocation = () => {
    setClinicLocation(null);
    alert('تم إزالة الموقع');
  };

  const copyProfileURL = async () => {
    try {
      setCopyingQR(true);
      
      const profileURL = `${window.location.origin}/p/${doctor.username}`;
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(profileURL);
          showMessage('تم نسخ رابط الملف الشخصي بنجاح');
          return;
        } catch (clipboardError) {
          console.warn('Clipboard API failed, trying fallback:', clipboardError);
        }
      }
      
      // Fallback method for Electron and older browsers
      const textArea = document.createElement('textarea');
      textArea.value = profileURL;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showMessage('تم نسخ رابط الملف الشخصي بنجاح');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        showMessage('فشل في نسخ الرابط. يرجى نسخ الرابط يدوياً', true);
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Error copying profile URL:', error);
      showMessage('فشل في نسخ الرابط', true);
    } finally {
      setCopyingQR(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctor.full_name.trim()) {
      showMessage('Full name is required', true);
      return;
    }

    try {
      setSaving(true);
            
      const formData = new FormData();
      
      // Add scalar fields to formData (skip arrays like open_days)
      Object.keys(doctor).forEach(key => {
        if (key === 'open_days') return;
        if (doctor[key] !== null && doctor[key] !== undefined) {
          formData.append(key, doctor[key]);
        }
      });

      // Add open_days as array items for Laravel (open_days[])
      if (Array.isArray(doctor.open_days)) {
        doctor.open_days.forEach(d => formData.append('open_days[]', d));
      }

      // Add open_day_times as nested fields: open_day_times[Day][opening_time]
      if (doctor.open_day_times && typeof doctor.open_day_times === 'object') {
        DAYS.forEach((day) => {
          if (doctor.open_days.includes(day) && doctor.open_day_times[day]) {
            const ot = doctor.open_day_times[day].opening_time || '';
            const ct = doctor.open_day_times[day].closing_time || '';
            if (ot !== '') formData.append(`open_day_times[${day}][opening_time]`, ot);
            if (ct !== '') formData.append(`open_day_times[${day}][closing_time]`, ct);
          }
        });
      }
      
      // Add clinic location coordinates (send null if cleared)
      if (clinicLocation) {
        formData.append('clinic_latitude', clinicLocation.latitude);
        formData.append('clinic_longitude', clinicLocation.longitude);
      } else {
        formData.append('clinic_latitude', '');
        formData.append('clinic_longitude', '');
      }
      
      // Ensure required fields have default values
      if (!formData.has('is_phone')) formData.append('is_phone', true);
      
      await axios.post(
        `${API_URL}doctor/update-doctor`,
        formData,
        {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json'
          }
        }
      );
      
      showMessage('Profile updated successfully');
    } catch (error) {
      console.error('Error updating doctor information:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      showMessage(errorMessage, true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-list-container" dir="rtl">
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={3000} 
          autohide
          className={isError ? 'bg-danger text-white' : 'bg-success text-white'}
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </div>
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="card patient-list-card">
              <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                <div className="w-100 d-flex justify-content-between align-items-center flex-nowrap" dir="rtl">
                  <div className="d-flex align-items-center">
                    <span className="fw-semibold">بيانات الملف</span>
                  </div>
                  
                </div>
              </div>
              <div className="card-body patient-list-body text-end">
                <form onSubmit={handleSubmit}>
                  {/* Tabs Header */}
                  <div className="mb-4 w-100">
                  <ul className="nav nav-pills tabs-underline w-100 d-flex justify-content-start" dir="rtl">
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}>
                        <i className="fas fa-user ms-2"></i>المعلومات 
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'clinic' ? 'active' : ''}`} onClick={() => setActiveTab('clinic')}>
                        <i className="fas fa-clinic-medical ms-2"></i>العمل  
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'social' ? 'active' : ''}`} onClick={() => setActiveTab('social')}>
                        <i className="fas fa-share-alt ms-2"></i> التواصل 
                      </button>
                    </li>
                    {/* Profile tab removed and merged into Social tab */}
                  </ul>
                  </div>

                  {/* Tabs Content */}
                  {activeTab === 'personal' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-3 text-end">
                        <i className="fas fa-user me-2"></i>
                        المعلومات الشخصية
                      </h5>
                      <div className="mb-3">
                        <label htmlFor="full_name" className="form-label text-end d-block">الاسم الكامل *</label>
                        <input
                          type="text"
                          className="form-control text-end"
                          id="full_name"
                          name="full_name"
                          value={doctor.full_name}
                          onChange={handleInputChange}
                          required
                          dir="rtl"
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="brief" className="form-label text-end d-block">الوصف </label>
                        <textarea
                          className="form-control text-end"
                          id="brief"
                          name="brief"
                          rows="3"
                          value={doctor.brief || ''}
                          onChange={handleInputChange}
                          placeholder="يرجى كتابة نبذة"
                          dir="rtl"
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="phone_number" className="form-label text-end d-block">رقم الهاتف</label>
                        <input
                          type="tel"
                          className="form-control text-end"
                          id="phone_number"
                          name="phone_number"
                          value={doctor.phone_number}
                          onChange={handleInputChange}
                          dir="ltr"
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'professional' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-3 text-end">
                        <i className="fas fa-certificate me-2"></i>
                        المعلومات المهنية
                      </h5>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="license_number" className="form-label text-end d-block">رقم الترخيص</label>
                          <input
                            type="text"
                            className="form-control text-end"
                            id="license_number"
                            name="license_number"
                            value={doctor.license_number || ''}
                            onChange={handleInputChange}
                            placeholder="أدخل رقم الترخيص"
                            dir="ltr"
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="license_date" className="form-label text-end d-block">تاريخ الترخيص</label>
                          <input
                            type="date"
                            className="form-control text-end"
                            id="license_date"
                            name="license_date"
                            value={doctor.license_date || ''}
                            onChange={handleInputChange}
                            dir="ltr"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'clinic' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-4 text-end">
                        <i className="fas fa-clinic-medical me-2"></i>
                        معلومات
                      </h5>

                      {/* Section: Address & Map */}
                      <div className="mb-4 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-end">
                          <i className="fas fa-map-marker-alt me-2"></i>
                          العنوان والموقع
                        </h6>
                        <div className="mb-3">
                          <label htmlFor="clinic_address" className="form-label text-end d-block">العنوان</label>
                          <textarea
                            className="form-control text-end"
                            id="clinic_address"
                            name="clinic_address"
                            rows="3"
                            value={doctor.clinic_address || ''}
                            onChange={handleInputChange}
                            placeholder="أدخل العنوان"
                            dir="rtl"
                          ></textarea>
                        </div>
                        <div>
                          <label className="form-label text-end d-block mb-3">موقع على الخريطة</label>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={resetToCurrentLocation}
                                disabled={locationLoading}
                              >
                                {locationLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    جاري التحديد...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-crosshairs me-1"></i>
                                    استخدام الموقع الحالي
                                  </>
                                )}
                              </button>
                              {clinicLocation && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={clearLocation}
                                >
                                  <i className="fas fa-times me-1"></i>
                                  إزالة الموقع
                                </button>
                              )}
                            </div>
                            <small className="text-muted text-end">انقر على الخريطة لاختيار الموقع</small>
                          </div>
                          <div style={{ height: '300px', border: '2px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
                            {(clinicLocation || currentLocation) ? (
                              <GoogleMapComponent
                                center={clinicLocation ? { lat: clinicLocation.latitude, lng: clinicLocation.longitude } : { lat: currentLocation.latitude, lng: currentLocation.longitude }}
                                clinicLocation={clinicLocation}
                                onMapClick={(location) => {
                                  setClinicLocation(location);
                                }}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                                <div className="text-center text-muted">
                                  <i className="fas fa-map-marker-alt fa-3x mb-3"></i>
                                  <p>لم يتم تحديد الموقع بعد</p>
                                  <p>استخدم زر "استخدام الموقع الحالي" أو انقر على الخريطة لتحديد الموقع</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="mt-2 text-end">
                            <small className="text-muted">
                              {clinicLocation ? (
                                <>الموقع الحالي: {clinicLocation.latitude.toFixed(6)}, {clinicLocation.longitude.toFixed(6)}</>
                              ) : (
                                'لم يتم تحديد موقع'
                              )}
                            </small>
                          </div>
                        </div>
                      </div>
                      

                      {/* Section: Open Days */}
                      <div className="mb-4 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-end">
                          <i className="far fa-calendar-check me-2"></i>
                          أيام الدوام
                        </h6>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                            <label className="form-label mb-2">أيام دوام</label>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-light btn-sm" onClick={selectAllOpenDays}>تحديد الكل</button>
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearOpenDays}>مسح</button>
                            </div>
                          </div>
                          <div className="open-days-group text-end">
                            {DAYS.map((day) => (
                              <span key={day} className="me-1 mb-2 d-inline-block">
                                <input
                                  type="checkbox"
                                  className="btn-check"
                                  id={`open_day_${day}`}
                                  autoComplete="off"
                                  checked={doctor.open_days?.includes(day) || false}
                                  onChange={() => toggleOpenDay(day)}
                                />
                                <label className="btn btn-outline-primary btn-sm rounded-pill" htmlFor={`open_day_${day}`}>
                                  <i className="far fa-calendar-check ms-1"></i>
                                  {DAY_LABELS[day] || day}
                                </label>
                              </span>
                            ))}
                          </div>
                          <small className="text-muted d-block text-end mt-1">حدد الأيام التي تعمل فيها .</small>
                        </div>

                        {doctor.open_days?.length > 0 && (
                          <div className="mb-0">
                            <label className="form-label text-end d-block">أوقات الدوام لكل يوم</label>
                            <div className="row g-2">
                              {doctor.open_days.map((day) => (
                                <div className="col-12" key={`times-${day}`}>
                                  <div className="p-2 border rounded d-flex flex-wrap align-items-center justify-content-between" dir="rtl">
                                    <strong className="mb-2 mb-sm-0">{DAY_LABELS[day] || day}</strong>
                                    <div className="d-flex gap-2 align-items-center flex-wrap" dir="ltr">
                                      <div className="input-group input-group-sm" style={{ minWidth: 140 }}>
                                        <span className="input-group-text"><i className="far fa-clock"></i></span>
                                        <select
                                          className="form-select"
                                          value={doctor.open_day_times?.[day]?.closing_time || ''}
                                          onChange={(e) => handleTimeChange(day, 'closing_time', e.target.value)}
                                        >
                                          <option value="">—</option>
                                          {doctor.open_day_times?.[day]?.closing_time && !HOUR_OPTIONS.includes(doctor.open_day_times[day].closing_time) && (
                                            <option value={doctor.open_day_times[day].closing_time}>
                                              {to12h(doctor.open_day_times[day].closing_time)}
                                            </option>
                                          )}
                                          {HOUR_OPTIONS.map((h) => (
                                            <option key={`close-${day}-${h}`} value={h}>{to12h(h)}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <span className="mx-1">-</span>
                                      <div className="input-group input-group-sm" style={{ minWidth: 140 }}>
                                        <span className="input-group-text"><i className="far fa-clock"></i></span>
                                        <select
                                          className="form-select"
                                          value={doctor.open_day_times?.[day]?.opening_time || ''}
                                          onChange={(e) => handleTimeChange(day, 'opening_time', e.target.value)}
                                        >
                                          <option value="">—</option>
                                          {doctor.open_day_times?.[day]?.opening_time && !HOUR_OPTIONS.includes(doctor.open_day_times[day].opening_time) && (
                                            <option value={doctor.open_day_times[day].opening_time}>
                                              {to12h(doctor.open_day_times[day].opening_time)}
                                            </option>
                                          )}
                                          {HOUR_OPTIONS.map((h) => (
                                            <option key={`open-${day}-${h}`} value={h}>{to12h(h)}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <small className="text-muted d-block text-end mt-1">يمكنك ترك الأوقات فارغة إذا لم تكن محددة.</small>
                          </div>
                        )}
                      </div>

                      {/* Section: Closed Dates */}
                      <div className="mb-0 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-end">
                          <i className="far fa-calendar-times me-2"></i>
                          أيام الإغلاق (مثل ايام العطل الرسمية او الاجازات)
                        </h6>
                        <div className="d-flex gap-2 align-items-center flex-wrap" dir="rtl">
                          <input
                            type="date"
                            className="form-control text-end"
                            style={{ maxWidth: '220px' }}
                            value={newClosedDate}
                            onChange={(e) => setNewClosedDate(e.target.value)}
                          />
                          <button type="button" className="btn btn-primary" onClick={handleAddClosedDate}>
                            <i className="fas fa-plus ms-1"></i>
                            إضافة تاريخ مغلق
                          </button>
                        </div>
                        <small className="text-muted d-block text-end mt-1">ستظهر هذه التواريخ كغير متاحة للحجز.</small>
                        <div className="mt-3">
                          {closedDates.length === 0 ? (
                            <div className="text-muted text-end">لا توجد تواريخ مغلقة</div>
                          ) : (
                            <ul className="list-group">
                              {closedDates.map((item) => (
                                <li key={item.id ?? item.date} className="list-group-item d-flex justify-content-between align-items-center" dir="ltr">
                                  <span>
                                    <i className="far fa-calendar-times me-2 text-danger"></i>
                                    {item.date}
                                  </span>
                                  {item.id ? (
                                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteClosedDate(item.id)}>
                                      <i className="fas fa-trash-alt"></i>
                                    </button>
                                  ) : (
                                    <span className="badge bg-secondary">محفوظ من الملف</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'social' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-3 text-end">
                        <i className="fas fa-share-alt me-2"></i>
                        وسائل التواصل الاجتماعي
                      </h5>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="facebook_url" className="form-label text-end d-block">
                            <i className="fab fa-facebook me-2" style={{color: '#1877F2'}}></i>
                            رابط الفيسبوك
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="facebook_url"
                            name="facebook_url"
                            value={doctor.facebook_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://facebook.com/your-profile"
                            dir="ltr"
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="instagram_url" className="form-label text-end d-block">
                            <i className="fab fa-instagram me-2" style={{color: '#E4405F'}}></i>
                            رابط الإنستغرام
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="instagram_url"
                            name="instagram_url"
                            value={doctor.instagram_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://instagram.com/your-profile"
                            dir="ltr"
                          />
                        </div>
                      </div>

                      {doctor.username && (
                        <div className="search-filters-section mt-4">
                          <h6 className="mb-3 text-end">
                            <i className="fas fa-link me-2"></i>
                            رابط الملف الشخصي
                          </h6>
                          <div className="profile-url-section p-3 bg-light border rounded">
                            <p className="text-muted mb-3 text-end">
                              يمكنك نسخ ومشاركة هذا الرابط لعرض ملفك الشخصي
                            </p>
                            <div
                              className="profile-url-display p-3 mb-3 bg-white border rounded"
                              style={{
                                direction: 'ltr',
                                textAlign: 'left',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                wordBreak: 'break-all',
                                backgroundColor: '#ffffff',
                                borderColor: '#dee2e6'
                              }}
                            >
                              {`${window.location.origin}/p/${doctor.username}`}
                            </div>
                            <div className="d-flex gap-2 justify-content-end">
                              <a
                                href={`${window.location.origin}/p/${doctor.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-info"
                                style={{ borderRadius: '8px', padding: '0.75rem 1.5rem' }}
                              >
                                <i className="fas fa-external-link-alt me-2"></i>
                                عرض الملف الشخصي
                              </a>
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={copyProfileURL}
                                disabled={copyingQR}
                                style={{ borderRadius: '8px', padding: '0.75rem 1.5rem' }}
                              >
                                {copyingQR ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    جاري النسخ...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-copy me-2"></i>
                                    نسخ الرابط
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Profile tab removed; content merged into Social tab below */}

                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      type="submit"
                      className="btn ti-save-btn"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          حفظ 
                        </>
                      )}
                    </button>
                  </div>
              </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestInformation;
